import { useId } from 'react';
import { useI18n } from '../lib/i18n';

/**
 * Lightweight, dependency-free charts drawn as inline SVG so they inherit the
 * ERP design tokens and stay crisp in light/dark. Two shapes cover the Painel:
 * a monthly bar chart (contratos iniciados/terminados) and a year-over-year
 * line chart with a dashed forecast tail (ano anterior vs atual).
 */

const MONTHS_PT = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];
const MONTHS_EN = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

/** Short month abbreviations for the active language (axis ticks). */
function useMonths(): string[] {
  return useI18n().lang === 'pt' ? MONTHS_PT : MONTHS_EN;
}

/** 'yyyy-mm' → short label ("jan", and "jan 25" on January to anchor the year). */
function monthLabel(key: string, months: string[]): string {
  const [y, m] = key.split('-');
  const idx = Number(m) - 1;
  const name = months[idx] ?? m;
  return idx === 0 ? `${name} ${y.slice(2)}` : name;
}

const VB_W = 340;
const VB_H = 150;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 22;

export interface BarPoint {
  key: string;
  /** 'yyyy-mm' is converted to a short month label; any other key is shown as-is. */
  value: number;
}

/** Monthly bar chart with a soft baseline and sparse month ticks. */
export function BarChart({
  data,
  color = 'var(--accent)',
  ariaLabel,
}: {
  data: BarPoint[];
  color?: string;
  ariaLabel: string;
}) {
  const months = useMonths();
  const max = Math.max(1, ...data.map((d) => d.value));
  const n = data.length || 1;
  const plotW = VB_W - PAD_L - PAD_R;
  const plotH = VB_H - PAD_T - PAD_B;
  const slot = plotW / n;
  const barW = Math.min(22, slot * 0.6);
  const baseY = VB_H - PAD_B;

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="none"
    >
      <line
        x1={PAD_L}
        y1={baseY}
        x2={VB_W - PAD_R}
        y2={baseY}
        stroke="var(--border)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      {data.map((d, i) => {
        const h = (d.value / max) * plotH;
        const x = PAD_L + i * slot + (slot - barW) / 2;
        const y = baseY - h;
        const showTick = i % 2 === n % 2; // ~6 ticks, ending on the latest month
        return (
          <g key={d.key}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={Math.max(h, d.value > 0 ? 2 : 0)}
              rx="2.5"
              fill={color}
            >
              <title>{`${monthLabel(d.key, months)}: ${d.value}`}</title>
            </rect>
            {d.value > 0 && (
              <text x={x + barW / 2} y={y - 3} className="chart-val" textAnchor="middle">
                {d.value}
              </text>
            )}
            {showTick && (
              <text x={x + barW / 2} y={VB_H - 7} className="chart-tick" textAnchor="middle">
                {monthLabel(d.key, months).split(' ')[0]}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export interface CategoryPoint {
  label: string;
  value: number;
}

/** Vertical bars for labelled categories (e.g. publications per year). */
export function CategoryBarChart({
  data,
  color = 'var(--accent)',
  ariaLabel,
}: {
  data: CategoryPoint[];
  color?: string;
  ariaLabel: string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const n = data.length || 1;
  const plotW = VB_W - PAD_L - PAD_R;
  const plotH = VB_H - PAD_T - PAD_B;
  const slot = plotW / n;
  const barW = Math.min(26, slot * 0.62);
  const baseY = VB_H - PAD_B;
  const tickEvery = n > 8 ? 2 : 1;

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="none"
    >
      <line
        x1={PAD_L}
        y1={baseY}
        x2={VB_W - PAD_R}
        y2={baseY}
        stroke="var(--border)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      {data.map((d, i) => {
        const h = (d.value / max) * plotH;
        const x = PAD_L + i * slot + (slot - barW) / 2;
        const y = baseY - h;
        return (
          <g key={d.label + i}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={Math.max(h, d.value > 0 ? 2 : 0)}
              rx="2.5"
              fill={color}
            >
              <title>{`${d.label}: ${d.value}`}</title>
            </rect>
            {d.value > 0 && (
              <text x={x + barW / 2} y={y - 3} className="chart-val" textAnchor="middle">
                {d.value}
              </text>
            )}
            {i % tickEvery === (n - 1) % tickEvery && (
              <text x={x + barW / 2} y={VB_H - 7} className="chart-tick" textAnchor="middle">
                {d.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Horizontal bars with labels — for long categories (teams, researchers). */
export function HBarChart({ data, ariaLabel }: { data: CategoryPoint[]; ariaLabel: string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="hbar" role="img" aria-label={ariaLabel}>
      {data.map((d, i) => (
        <div className="hbar-row" key={d.label + i} title={`${d.label}: ${d.value}`}>
          <span className="hbar-label">{d.label}</span>
          <span className="hbar-track">
            <span className="hbar-fill" style={{ width: `${(d.value / max) * 100}%` }} />
          </span>
          <span className="hbar-val">{d.value}</span>
        </div>
      ))}
      {data.length === 0 && <div className="panel-empty">Sem dados.</div>}
    </div>
  );
}

const DONUT_COLORS: Record<string, string> = {
  Q1: 'var(--ok)',
  Q2: 'var(--info)',
  Q3: 'var(--warn)',
  Q4: 'var(--bad)',
};

/** Donut for a small distribution (e.g. the Q1–Q4 quartile split). */
export function DonutChart({ data, ariaLabel }: { data: CategoryPoint[]; ariaLabel: string }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = 52;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="donut" role="img" aria-label={ariaLabel}>
      <svg viewBox="0 0 140 140" className="donut-svg">
        <g transform="translate(70 70) rotate(-90)">
          <circle r={r} fill="none" stroke="var(--surface-2)" strokeWidth="18" />
          {data.map((d, i) => {
            const frac = d.value / total;
            const dash = frac * c;
            const seg = (
              <circle
                key={d.label + i}
                r={r}
                fill="none"
                stroke={DONUT_COLORS[d.label] ?? 'var(--accent)'}
                strokeWidth="18"
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-offset}
              >
                <title>{`${d.label}: ${d.value}`}</title>
              </circle>
            );
            offset += dash;
            return seg;
          })}
        </g>
        <text x="70" y="74" textAnchor="middle" className="donut-total">
          {total}
        </text>
      </svg>
      <div className="donut-legend">
        {data.map((d) => (
          <span key={d.label}>
            <i style={{ background: DONUT_COLORS[d.label] ?? 'var(--accent)' }} />
            {d.label} · {d.value}
          </span>
        ))}
      </div>
    </div>
  );
}

export interface YearPoint {
  month: number; // 1–12
  previous: number;
  current: number;
  forecast: boolean;
}

/** Year-over-year line chart: previous (muted) vs current (accent, dashed tail). */
export function YearCompareChart({ data, ariaLabel }: { data: YearPoint[]; ariaLabel: string }) {
  const uid = useId();
  const months = useMonths();
  const max = Math.max(1, ...data.flatMap((d) => [d.previous, d.current]));
  const plotW = VB_W - PAD_L - PAD_R;
  const plotH = VB_H - PAD_T - PAD_B;
  const baseY = VB_H - PAD_B;
  const n = data.length || 1;
  const x = (i: number) => PAD_L + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => baseY - (v / max) * plotH;

  const prevPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(d.previous)}`).join(' ');
  // Current line: solid up to the last actual point, dashed through the forecast.
  const lastActual = data.reduce((acc, d, i) => (d.forecast ? acc : i), 0);
  const solid = data
    .slice(0, lastActual + 1)
    .map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(d.current)}`)
    .join(' ');
  const dashed = data
    .slice(lastActual)
    .map((d, i) => `${i === 0 ? 'M' : 'L'}${x(lastActual + i)} ${y(d.current)}`)
    .join(' ');

  return (
    <svg className="chart" viewBox={`0 0 ${VB_W} ${VB_H}`} role="img" aria-label={ariaLabel}>
      <line
        x1={PAD_L}
        y1={baseY}
        x2={VB_W - PAD_R}
        y2={baseY}
        stroke="var(--border)"
        strokeWidth="1"
      />
      <path
        d={prevPath}
        fill="none"
        stroke="var(--text-3)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
      <path
        d={solid}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {dashed && (
        <path
          d={dashed}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2.4"
          strokeDasharray="4 4"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.8"
        />
      )}
      {data.map((d, i) =>
        i % 2 === 0 ? (
          <text
            key={`t${uid}${i}`}
            x={x(i)}
            y={VB_H - 7}
            className="chart-tick"
            textAnchor="middle"
          >
            {months[d.month - 1]}
          </text>
        ) : null,
      )}
    </svg>
  );
}
